import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ArticleCard from '@/components/ArticleCard';
import { Loader2, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setPage(1); }, [q]);

  useEffect(() => {
    if (!q) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API}/public/articles?q=${encodeURIComponent(q)}&page=${page}&limit=12&lang=${i18n.language}`)
      .then(r => r.json())
      .then(d => { setArticles(d.articles || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [q, page, i18n.language]);

  const totalPages = Math.ceil(total / 12);

  return (
    <>
      <Helmet><title>{`${t('search.title')}: ${q} \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="search-page">
        <div className="flex items-center gap-3 mb-8">
          <Search size={24} className="text-gray-400" />
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{q ? `${t('search.results_for')} "${q}"` : t('search.title')}</h1>
            {total > 0 && <p className="text-sm text-gray-500">{t('pagination.total')}: {total}</p>}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : !q ? (
          <p className="text-center py-12 text-gray-400">{t('search.placeholder')}</p>
        ) : articles.length === 0 ? (
          <p className="text-center py-12 text-gray-400">{t('search.no_results')} "{q}"</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map(a => <ArticleCard key={a.id} article={a} />)}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8 text-sm">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-4 py-2 bg-gray-100 dark:bg-[#111827] rounded-lg disabled:opacity-30">{t('pagination.prev')}</button>
                <span className="text-gray-500">{t('pagination.page')} {page} {t('pagination.of')} {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-4 py-2 bg-gray-100 dark:bg-[#111827] rounded-lg disabled:opacity-30">{t('pagination.next')}</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
